import { useMemo, useState } from "react";
import { Action } from "../components/Actions";
import { EditorialShell } from "../components/EditorialShell";
import { TrackList } from "../components/TrackList";
import { ALBUMS } from "../data/albums";
import { songsByAlbum } from "../data";
import { compareSelections, decodeMatchSelections, encodeMatchSelections, matchInvitationUrl, matchResultUrl } from "../logic/match";
import type { SelectionMap } from "../types";
import "./match.css";

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement("textarea");
  input.value = text;
  input.style.position = "fixed";
  input.style.opacity = "0";
  input.setAttribute("readonly", "");
  document.body.appendChild(input);
  input.select();
  const success = document.execCommand("copy");
  input.remove();
  if (!success) throw new Error("复制不可用");
}

async function shareText(title: string, text: string, url: string, copied: () => void) {
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }
  await copyText(`${text}\n${url}`);
  copied();
}

function InvalidInvite({ onBack }: { onBack(): void }) {
  return <EditorialShell className="match-shell"><section className="match-panel">
    <p className="match-kicker">WMLSTI · 好友默契度</p>
    <h1>这条邀请已失效</h1>
    <p className="match-muted">链接里的歌单无法识别，请让朋友重新生成邀请。</p>
    <Action onClick={onBack}>返回首页</Action>
  </section></EditorialShell>;
}

export function MatchInviteScreen({ selections, onBack }: { selections: SelectionMap; onBack(): void }) {
  const [message, setMessage] = useState("");
  const code = encodeMatchSelections(selections);
  const url = matchInvitationUrl(code);

  async function share() {
    try {
      await shareText("WMLSTI · 九首歌的默契", "我选好了九首五月天的歌，来看看我们有多默契。", url, () => setMessage("邀请链接已复制"));
    } catch { setMessage("分享未成功，可以手动复制下方链接"); }
  }

  return <EditorialShell className="match-shell">
    <section className="match-panel page-enter">
      <p className="match-kicker">WMLSTI / FOR FRIENDS</p>
      <h1>九首歌的默契</h1>
      <p className="match-lead">你的九首歌已经装进邀请里。发给朋友，让 TA 从同样的九张专辑各选一首，完成后就能看到你们的默契度。</p>
      <div className="match-invite-cover" aria-hidden="true">
        {ALBUMS.map((album, i) => <img key={album.name} src={album.cover} alt="" style={{ zIndex: 10 - i }} />)}
      </div>
      <div className="match-actions">
        <Action onClick={share}>分享给朋友</Action>
        <Action tone="secondary" onClick={async () => { try { await copyText(url); setMessage("邀请链接已复制"); } catch { setMessage("复制失败，请手动选择链接"); } }}>复制邀请链接</Action>
        <Action tone="text" onClick={onBack}>← 返回结果卡片</Action>
      </div>
      <input className="match-url" aria-label="好友邀请链接" readOnly value={url} onFocus={(event) => event.currentTarget.select()} />
      <p className="match-feedback" role="status">{message}</p>
      <p className="match-note">链接中只包含你的九首选择。好友需要把比较结果发回来，你才能看到双方的分数；目前不收集账号或上传数据。</p>
    </section>
  </EditorialShell>;
}

export function MatchFlowScreen({ ownerCode, onNavigate, onBack }: {
  ownerCode: string; onNavigate(path: string): void; onBack(): void;
}) {
  const valid = useMemo(() => decodeMatchSelections(ownerCode), [ownerCode]);
  const [index, setIndex] = useState(-1);
  const [choices, setChoices] = useState<SelectionMap>({});
  if (!valid) return <InvalidInvite onBack={onBack} />;
  const album = ALBUMS[Math.max(0, index)];
  const songs = songsByAlbum.get(album.name) ?? [];

  if (index === -1) {
    return <EditorialShell className="match-shell">
      <section className="match-panel match-welcome page-enter">
        <p className="match-kicker">WMLSTI / 好友邀请</p>
        <h1>你们会选中多少首同样的歌？</h1>
        <p className="match-lead">有朋友留下了自己的九首五月天。你也从九张专辑各选一首，完成以后对照看看。</p>
        <div className="match-invite-cover" aria-hidden="true">{ALBUMS.map((item) => <img key={item.name} src={item.cover} alt="" />)}</div>
        <div className="match-actions"><Action onClick={() => setIndex(0)}>开始选歌</Action><Action tone="text" onClick={onBack}>返回 WMLSTI</Action></div>
      </section>
    </EditorialShell>;
  }

  const selected = choices[album.name];
  const finish = () => {
    if (!selected) return;
    if (index < ALBUMS.length - 1) {
      setIndex(index + 1);
    } else {
      onNavigate(`/match-result/${ownerCode}/${encodeMatchSelections(choices)}`);
    }
  };
  return <EditorialShell className="match-shell">
    <section className="match-panel match-question page-enter" key={index}>
      <p className="match-kicker">FRIEND'S PLAYLIST / {String(index + 1).padStart(2, "0")} — 09</p>
      <div className="match-progress" role="progressbar" aria-label="选歌进度" aria-valuenow={index + 1} aria-valuemin={0} aria-valuemax={9}><span style={{ width: `${((index + 1) / 9) * 100}%` }} /></div>
      <div className="match-album-heading"><div><p>{album.year} · 第 {index + 1} 张专辑</p><h1>{album.name}</h1><p className="match-muted">选一首你最喜欢的，或最像你的。</p></div><img src={album.cover} alt={album.name + "专辑封面"} /></div>
      <TrackList songs={songs} selected={selected} cover={album.cover} onSelect={(song) => setChoices((previous) => ({ ...previous, [album.name]: song }))} />
      <div className="match-actions match-actions--row"><Action tone="secondary" onClick={() => setIndex(index - 1)}>上一步</Action><Action disabled={!selected} onClick={finish}>{index === 8 ? "查看默契度" : "下一张专辑"}</Action></div>
    </section>
  </EditorialShell>;
}

export function MatchResultScreen({ ownerCode, friendCode, onBack }: { ownerCode: string; friendCode: string; onBack(): void }) {
  const [message, setMessage] = useState("");
  const owner = useMemo(() => decodeMatchSelections(ownerCode), [ownerCode]);
  const friend = useMemo(() => decodeMatchSelections(friendCode), [friendCode]);
  const comparison = useMemo(() => owner && friend ? compareSelections(owner, friend) : null, [owner, friend]);
  if (!comparison) return <InvalidInvite onBack={onBack} />;
  const url = matchResultUrl(ownerCode, friendCode);
  const { score, exactMatches } = comparison;

  async function share() {
    try {
      await shareText("WMLSTI · 好友默契度", `我们的五月天选歌默契度是 ${score}%，九张专辑有 ${exactMatches} 首选得一样。`, url, () => setMessage("结果链接已复制，发回给朋友即可"));
    } catch { setMessage("分享失败，请手动复制下方链接"); }
  }
  return <EditorialShell className="match-shell">
    <section className="match-panel match-results page-enter">
      <p className="match-kicker">WMLSTI · 九首歌的默契</p>
      <p className="match-score-caption">你们的选歌默契度</p>
      <h1 className="match-score">{comparison.score}<span>%</span></h1>
      <p className="match-score-sub">9 张专辑，你们有 <strong>{comparison.exactMatches} 首</strong>选得一样。</p>
      <div className="match-persona-row"><div><small>发起人</small><strong>{comparison.ownerPersona}</strong></div><span>×</span><div><small>你</small><strong>{comparison.friendPersona}</strong></div></div>
      <h2 className="match-subheading">我们的九首歌</h2>
      <div className="match-song-columns"><span>专辑</span><span>发起人的选择</span><span>我的选择</span></div>
      <ol className="match-song-list">
        {comparison.rows.map((row) => <li className={row.same ? "is-same" : ""} key={row.album.name}>
          <img src={row.album.cover} alt="" />
          <div><small>{row.album.name}</small><strong>{row.same ? "选中同一首" : "不同的选择"}</strong></div>
          <p>{row.ownerSong}</p><p>{row.friendSong}</p>
        </li>)}
      </ol>
      <h2 className="match-subheading">六维倾向</h2>
      <div className="match-dimensions">{comparison.dimensions.map((item) => <div key={item.name}><span>{item.name}</span><span>{item.owner}</span><span>{item.friend}</span></div>)}</div>
      <div className="match-actions"><Action onClick={share}>把结果发回给朋友</Action><Action tone="secondary" onClick={async () => { try { await copyText(url); setMessage("结果链接已复制"); } catch { setMessage("复制失败，请手动选择链接"); } }}>复制比较链接</Action><Action tone="text" onClick={onBack}>回到 WMLSTI</Action></div>
      <input className="match-url" aria-label="双方比较结果链接" readOnly value={url} onFocus={(event) => event.currentTarget.select()} />
      <p className="match-feedback" role="status">{message}</p>
      <p className="match-note">趣味算法：相同歌曲占 60%，六维百分位接近程度占 40%。默契度只用于本测试的选歌比较，不代表心理测量结果。链接包含双方歌单，持有链接的人可以查看比较结果。</p>
    </section>
  </EditorialShell>;
}
